<template>
	<div class="issue-card" v-if="issue.length">
		<header class="card-header">
			<div class="header-content">
				<span class="issue-badge">{{ currIndexOfIssue + 1 }} {{ $t('of') }} {{ issue.length }}</span>
				<h3 class="card-title">{{ $t(title) }}</h3>
			</div>
			<div class="navigation-dots" v-if="issue.length > 1">
				<button
					v-for="(_, index) in issue"
					:key="index"
					class="dot"
					:class="{ active: index === currIndexOfIssue }"
					@click="currIndexOfIssue = index"
					:aria-label="`Go to issue ${index + 1}`"
				/>
			</div>
		</header>

		<!-- Main content area -->
		<div class="card-content">
			<!-- Navigation arrows -->
			<button
				v-if="issue[currIndexOfIssue - 1]"
				@click="sideArrowClicked('prev')"
				class="nav-button prev"
				:aria-label="$t('previous_issue')"
			>
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
					<path d="M15 18l-6-6 6-6" />
				</svg>
			</button>

			<!-- Issue content -->
			<div class="issue-content">
				<!-- Icon section -->
				<div class="issue-icon-container">
					<div class="icon-wrapper">
						<img
							class="issue-icon"
							v-if="issueIcon"
							:src="issueIcon"
							:alt="`${issue[currIndexOfIssue].servikName} icon`"
						/>
					</div>
				</div>

				<!-- Issue details -->
				<div class="issue-details">
					<div class="detail-group">
						<label class="detail-label">{{ $t('issue') }}</label>
						<div class="detail-value" :class="{ highlight: issue[currIndexOfIssue].type === 'complaint' }">
							<span
								class="truncated-text hoverable-text"
								@mouseenter="showFullText('issue')"
								@mouseleave="hideFullText('issue')"
							>
								{{
									issue[currIndexOfIssue].localServikName
										? issue[currIndexOfIssue].localServikName
										: issue[currIndexOfIssue].servikName
								}}
							</span>
							<div v-show="showTooltip.issue" class="custom-tooltip">
								{{
									issue[currIndexOfIssue].localServikName
										? issue[currIndexOfIssue].localServikName
										: issue[currIndexOfIssue].servikName
								}}
							</div>
						</div>
					</div>

					<div class="detail-group">
						<label class="detail-label">{{ $t('room') }}</label>
						<div class="detail-value" :class="{ highlight: issue[currIndexOfIssue].type === 'room' }">
							<span
								class="truncated-text hoverable-text"
								@mouseenter="showFullText('room')"
								@mouseleave="hideFullText('room')"
							>
								{{ issue[currIndexOfIssue].displayName }}
							</span>
							<div v-show="showTooltip.room" class="custom-tooltip">
								{{ issue[currIndexOfIssue].displayName }}
							</div>
						</div>
					</div>

					<div class="detail-group">
						<label class="detail-label">{{ $t('time') }}</label>
						<div class="detail-value" :class="{ highlight: issue[currIndexOfIssue].type === 'hour' }">
							<span
								class="truncated-text hoverable-text"
								@mouseenter="showFullText('time')"
								@mouseleave="hideFullText('time')"
							>
								{{ issue[currIndexOfIssue].hour | numberToHour }}
							</span>
							<div v-show="showTooltip.time" class="custom-tooltip">
								{{ issue[currIndexOfIssue].hour | numberToHour }}
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Next arrow -->
			<button
				v-if="issue[currIndexOfIssue + 1]"
				@click="sideArrowClicked('next')"
				class="nav-button next"
				:aria-label="$t('next_issue')"
			>
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
					<path d="M9 18l6-6-6-6" />
				</svg>
			</button>
		</div>

		<!-- Progress indicator -->
		<div class="progress-bar" v-if="issue.length > 1">
			<div class="progress-fill" :style="{ width: `${((currIndexOfIssue + 1) / issue.length) * 100}%` }"></div>
		</div>
	</div>

	<!-- Empty state -->
	<div class="empty-state-card" v-else>
		<div class="empty-content">
			<div class="empty-icon">
				<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
					<circle cx="12" cy="12" r="10" />
					<path d="M12 6v6l4 2" />
				</svg>
			</div>
			<h3 class="empty-title">{{ title }}</h3>
			<NoData height="80px" fontSize="0.9rem" class="no-data" />
		</div>
	</div>
</template>

<script>
import NoData from '@/modules/common/components/NoData'

export default {
	props: {
		title: { type: String, required: true },
		issue: { type: Array, required: true }
	},
	data() {
		return {
			currIndexOfIssue: 0,
			showTooltip: {
				issue: false,
				room: false,
				time: false
			}
		}
	},
	methods: {
		/**@param {'next' | 'prev'} action */
		sideArrowClicked(action) {
			if (action === 'next' && this.currIndexOfIssue < this.issue.length - 1) {
				this.currIndexOfIssue += 1
			} else if (action === 'prev' && this.currIndexOfIssue > 0) {
				this.currIndexOfIssue -= 1
			}
		},
		showFullText(field) {
			this.showTooltip[field] = true
		},
		hideFullText(field) {
			this.showTooltip[field] = false
		}
	},
	computed: {
		issueIcon() {
			const { servikName, iconUrl } = this.issue[this.currIndexOfIssue]
			const urlIssueName = servikName.toLowerCase().split(' ').join('_')
			let icon
			try {
				if (iconUrl) icon = iconUrl
				else icon = require(`@/assets/servik_icons/${urlIssueName}.svg`)
			} catch (err) {
				icon = require('@/assets/servik_icons/missing_toilet_paper.svg')
			}
			return icon
		}
	},
	components: { NoData }
}
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

$primary-color: #3b82f6;
$primary-light: #dbeafe;
$text-primary: #1f2937;
$text-secondary: #6b7280;
$text-muted: #9ca3af;
$background: #ffffff;
$border-color: #e5e7eb;
$hover-bg: #f9fafb;
$success-color: #10b981;
$warning-color: #f59e0b;

.issue-card {
	background: $background;
	border-radius: 16px;
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
	border: 1px solid $border-color;
	overflow: hidden;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	width: 100%;
	max-width: 100%;
	
	// Responsive height adjustments
	height: 240px;
	display: flex;
	flex-direction: column;
	animation: fadeInZoom 0.5s ease forwards;
	opacity: 0;

	@media (max-width: 640px) {
		height: auto;
		min-height: 280px;
		border-radius: 12px;
	}

	@media (min-width: 641px) and (max-width: 1024px) {
		height: 220px;
	}

	@media (min-width: 1025px) {
		height: 200px;
	}

	&:hover {
		box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
		transform: translateY(-2px);
	}
}

.card-header {
	padding: 16px 20px 12px;
	border-bottom: 1px solid $border-color;
	background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
	flex-shrink: 0;
	min-height: 70px;
	display: flex;
	flex-direction: column;
	justify-content: center;

	@media (max-width: 640px) {
		padding: 12px 16px 10px;
		min-height: 60px;
	}

	.header-content {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
		flex-wrap: wrap;

		@media (max-width: 640px) {
			gap: 6px;
			margin-bottom: 6px;
		}
	}

	.issue-badge {
		background: $primary-color;
		color: white;
		padding: 3px 10px;
		border-radius: 20px;
		font-size: 0.7rem;
		font-weight: 600;
		letter-spacing: 0.025em;
		white-space: nowrap;
		flex-shrink: 0;

		@media (max-width: 640px) {
			font-size: 0.65rem;
			padding: 2px 8px;
		}
	}

	.card-title {
		color: $text-primary;
		font-size: 1rem;
		font-weight: 600;
		margin: 0;
		line-height: 1.4;
		min-width: 0;
		word-break: break-word;

		@media (max-width: 640px) {
			font-size: 0.9rem;
		}
	}

	.navigation-dots {
		display: flex;
		gap: 6px;
		justify-content: center;
		flex-wrap: wrap;

		@media (max-width: 640px) {
			gap: 4px;
		}

		.dot {
			width: 6px;
			height: 6px;
			border-radius: 50%;
			border: none;
			background: $border-color;
			cursor: pointer;
			transition: all 0.2s ease;

			@media (max-width: 640px) {
				width: 5px;
				height: 5px;
			}

			&.active {
				background: $primary-color;
				transform: scale(1.2);
			}

			&:hover:not(.active) {
				background: $text-muted;
			}
		}
	}
}

.card-content {
	padding: 16px 20px;
	display: flex;
	align-items: center;
	gap: 16px;
	flex: 1;
	min-height: 0;

	@media (max-width: 640px) {
		padding: 16px;
		flex-direction: column;
		gap: 16px;
		align-items: stretch;
	}

	@media (min-width: 641px) and (max-width: 1024px) {
		padding: 14px 16px;
		gap: 12px;
	}
}

.nav-button {
	background: $hover-bg;
	border: 1px solid $border-color;
	border-radius: 10px;
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;
	color: $text-secondary;
	flex-shrink: 0;

	@media (max-width: 640px) {
		width: 32px;
		height: 32px;
		border-radius: 8px;
		
		// Position at bottom on mobile
		position: absolute;
		bottom: 16px;
		
		&.prev {
			left: 16px;
		}
		
		&.next {
			right: 16px;
		}
	}

	&:hover {
		background: $primary-light;
		border-color: $primary-color;
		color: $primary-color;
		transform: scale(1.05);
	}

	&:active {
		transform: scale(0.95);
	}

	svg {
		@media (max-width: 640px) {
			width: 16px;
			height: 16px;
		}
	}
}

.issue-content {
	flex: 1;
	display: flex;
	gap: 14px;
	align-items: center;
	min-width: 0;

	@media (max-width: 640px) {
		flex-direction: column;
		text-align: center;
		gap: 16px;
		width: 100%;
		position: relative;
		padding-bottom: 50px; // Space for nav buttons
	}

	@media (min-width: 641px) and (max-width: 1024px) {
		gap: 10px;
	}
}

.issue-icon-container {
	flex-shrink: 0;

	@media (max-width: 640px) {
		align-self: center;
	}

	.icon-wrapper {
		width: 52px;
		height: 52px;
		background: $primary-light;
		border-radius: 14px;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.3s ease;

		@media (max-width: 640px) {
			width: 48px;
			height: 48px;
			border-radius: 12px;
		}

		@media (min-width: 641px) and (max-width: 1024px) {
			width: 40px;
			height: 40px;
			border-radius: 10px;
		}

		&:hover {
			background: lighten($primary-light, 3%);
			transform: rotate(5deg) scale(1.05);
		}

		.issue-icon {
			width: 50px;
			height: 50px;
			object-fit: contain;

			@media (max-width: 640px) {
				width: 24px;
				height: 24px;
			}

			@media (min-width: 641px) and (max-width: 1024px) {
				width: 20px;
				height: 20px;
			}
		}
	}
}

.issue-details {
	flex: 1;
	display: grid;
	grid-template-columns: 1fr;
	gap: 16px;
	min-width: 0;
	overflow: hidden;

	@media (min-width: 640px) {
		grid-template-columns: repeat(3, 1fr);
		gap: 12px;
	}

	@media (min-width: 641px) and (max-width: 1024px) {
		gap: 8px;
	}

	@media (min-width: 1025px) {
		gap: 20px;
	}

	@media (max-width: 640px) {
		gap: 12px;
		width: 100%;
		text-align: left;
	}
}

.detail-group {
	min-width: 0;
	position: relative;
	padding: 0 4px;

	@media (max-width: 640px) {
		padding: 0;
		text-align: center;
	}

	&:not(:last-child)::after {
		content: '';
		position: absolute;
		right: 0;
		top: 50%;
		transform: translateY(-50%);
		width: 1px;
		height: 32px;
		background: $border-color;
		opacity: 0.4;

		@media (max-width: 640px) {
			display: none;
		}
	}

	.detail-label {
		display: block;
		font-size: 0.7rem;
		font-weight: 500;
		color: $text-muted;
		text-transform: uppercase;
		letter-spacing: 0.03em;
		margin-bottom: 3px;
		white-space: nowrap;

		@media (max-width: 640px) {
			font-size: 0.65rem;
		}
	}

	.detail-value {
		font-size: 0.9rem;
		font-weight: 500;
		color: $text-primary;
		line-height: 1.3;
		transition: all 0.2s ease;
		position: relative;
		min-width: 0;

		@media (max-width: 640px) {
			font-size: 0.85rem;
		}

		@media (min-width: 641px) and (max-width: 1024px) {
			font-size: 0.8rem;
		}

		.truncated-text {
			display: block;
			overflow: hidden;
			text-overflow: ellipsis;
			max-width: 100%;

			@media (max-width: 640px) {
				white-space: normal;
				overflow: visible;
				text-overflow: unset;
				display: -webkit-box;
				-webkit-box-orient: vertical;
				line-height: 1.3;
				max-height: 2.6em;
			}

			@media (min-width: 641px) {
				white-space: nowrap;
			}
		}

		.hoverable-text {
			cursor: pointer;

			&:hover {
				color: $primary-color;
			}
		}

		&.highlight {
			color: $primary-color;
			font-weight: 600;

			&::after {
				content: '';
				position: absolute;
				bottom: -2px;
				left: 0;
				width: 100%;
				height: 2px;
				background: $primary-color;
				border-radius: 1px;
			}
		}
	}
}

.custom-tooltip {
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: $text-primary;
	color: white;
	padding: 12px 16px;
	border-radius: 8px;
	font-size: 0.875rem;
	font-weight: 400;
	max-width: 320px;
	word-wrap: break-word;
	white-space: normal;
	line-height: 1.4;
	box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05);
	z-index: 9999;
	animation: tooltipSlideIn 0.2s ease-out;

	@media (max-width: 640px) {
		max-width: 280px;
		font-size: 0.8rem;
		padding: 10px 14px;
	}
}

@keyframes tooltipSlideIn {
	from {
		opacity: 0;
		transform: translate(-50%, -50%) scale(0.95);
	}
	to {
		opacity: 1;
		transform: translate(-50%, -50%) scale(1);
	}
}

.progress-bar {
	height: 3px;
	background: $border-color;
	position: relative;
	overflow: hidden;
	flex-shrink: 0;

	@media (max-width: 640px) {
		height: 4px;
	}

	.progress-fill {
		height: 100%;
		background: linear-gradient(90deg, $primary-color, lighten($primary-color, 10%));
		transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		position: relative;

		&::after {
			content: '';
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			width: 20px;
			background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3));
			animation: shimmer 2s infinite;
		}
	}
}

.empty-state-card {
	background: $background;
	border-radius: 16px;
	border: 2px dashed $border-color;
	padding: 48px 24px;
	text-align: center;
	width: 100%;
	max-width: 100%;
	height: 240px;
	display: flex;
	align-items: center;
	justify-content: center;

	@media (max-width: 640px) {
		height: auto;
		min-height: 200px;
		padding: 32px 16px;
		border-radius: 12px;
	}

	@media (min-width: 641px) and (max-width: 1024px) {
		height: 220px;
		padding: 40px 20px;
	}

	@media (min-width: 1025px) {
		height: 200px;
		max-width: 450px;
	}

	.empty-content {
		max-width: 320px;
		margin: 0 auto;

		@media (max-width: 640px) {
			max-width: 280px;
		}
	}

	.empty-icon {
		width: 48px;
		height: 48px;
		background: $hover-bg;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		margin: 0 auto 12px;
		color: $text-muted;

		@media (max-width: 640px) {
			width: 40px;
			height: 40px;
			margin-bottom: 10px;
		}

		svg {
			@media (max-width: 640px) {
				width: 32px;
				height: 32px;
			}
		}
	}

	.empty-title {
		color: $text-secondary;
		font-size: 1rem;
		font-weight: 500;
		margin: 0 0 8px;

		@media (max-width: 640px) {
			font-size: 0.9rem;
		}
	}
}

@keyframes shimmer {
	0% {
		transform: translateX(-100%);
	}
	100% {
		transform: translateX(200%);
	}
}

@keyframes fadeInZoom {
	0% {
		opacity: 0;
		transform: scale(0.95);
	}
	100% {
		opacity: 1;
		transform: scale(1);
	}
}

@media (max-width: 480px) {
	.issue-card {
		border-radius: 8px;
		min-height: 300px;
	}

	.card-header {
		padding: 10px 12px 8px;
		min-height: 55px;

		.issue-badge {
			font-size: 0.6rem;
			padding: 2px 6px;
		}

		.card-title {
			font-size: 0.85rem;
		}
	}

	.card-content {
		padding: 12px;
	}

	.nav-button {
		width: 28px;
		height: 28px;
		bottom: 12px;

		&.prev {
			left: 12px;
		}

		&.next {
			right: 12px;
		}

		svg {
			width: 14px;
			height: 14px;
		}
	}

	.issue-content {
		padding-bottom: 45px;
	}

	.issue-icon-container .icon-wrapper {
		width: 40px;
		height: 40px;

		.issue-icon {
			width: 20px;
			height: 20px;
		}
	}

	.detail-group .detail-value {
		font-size: 0.8rem;
	}

	.empty-state-card {
		min-height: 180px;
		padding: 24px 12px;
	}
}

// Print styles
@media print {
	.issue-card {
		box-shadow: none;
		border: 1px solid #000;
		margin-bottom: 20px;
		break-inside: avoid;
		height: auto;
	}

	.nav-button,
	.navigation-dots,
	.progress-bar {
		display: none;
	}

	.card-content {
		padding: 16px;
		flex-direction: row;
	}

	.issue-content {
		flex-direction: row;
		padding-bottom: 0;
	}

	.issue-details {
		grid-template-columns: repeat(3, 1fr);
	}
}

// High contrast mode support
@media (prefers-contrast: high) {
	.issue-card {
		border: 2px solid $text-primary;
	}

	.detail-value.highlight::after {
		height: 3px;
	}
}

// Reduced motion support
@media (prefers-reduced-motion: reduce) {
	.issue-card,
	.nav-button,
	.icon-wrapper,
	.progress-fill {
		transition: none;
	}

	.issue-card {
		animation: none;
		opacity: 1;
	}

	.custom-tooltip {
		animation: none;
	}

	.progress-fill::after {
		animation: none;
	}
}
</style>